import React, { useCallback, useEffect, useMemo } from 'react';

import qs from 'qs';
import { useDispatch } from 'react-redux';
import { RouteContext, useResource } from 'react-resource-router';

import { useApdexMeasuredBrowserNavigation } from 'src/components/apdex/with-apdex';
import { fetchProfileRepositoriesLanguages } from 'src/redux/profile/repositories/actions/fetch-repositories-languages';
import { pageUnload } from 'src/redux/profile/repositories/actions/index';
import { useHistory__UNSAFE } from 'src/router/hooks';
import { RepositoryFilters } from 'src/sections/profile/types';
import { ApdexTask } from 'src/types/apdex';

import Page from './components/page';
import { useWorkspaceRepositoriesQuery } from './hooks/use-workspace-repositories-query';
import { useWorkspaceRepositories } from './provider';
import {
  DEFAULT_REPOSITORIES_SORTING,
  workspaceRepositoriesResource,
} from './workspace-repositories-resource';

export const ProfileRepositoriesRoute: React.FC<RouteContext> = props => {
  const dispatch = useDispatch();
  const history = useHistory__UNSAFE();

  useApdexMeasuredBrowserNavigation(ApdexTask.ProfileRepositories, history);

  const { location, match, query } = props;
  const { pathname } = location;
  // Workspace slug should always be available in the URL, otherwise this component is
  // being rendered in the wrong context
  const workspaceSlug = match.params.workspaceSlug || '';

  const [
    { repositories, buildStatusesFetched },
    { setRepositories },
  ] = useWorkspaceRepositories();

  const repositoriesResource = useResource(workspaceRepositoriesResource);
  const { loading, error, data } = repositoriesResource;

  const showTableLoading = useMemo(
    () => loading || !(buildStatusesFetched || repositories.length),
    [repositories, buildStatusesFetched, loading]
  );

  const {
    setQuery,
    query: { filters, page, sort },
  } = useWorkspaceRepositoriesQuery();

  useEffect(() => {
    dispatch(fetchProfileRepositoriesLanguages(workspaceSlug));
    return () => {
      dispatch(pageUnload());
    };
    // Only called on once (on mount) to preserve original behavior (for now)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSortChange = useCallback(() => {
    setQuery({
      filters,
      sort: sort === DEFAULT_REPOSITORIES_SORTING ? 'updated_on' : undefined,
    });
  }, [filters, setQuery, sort]);

  const handleFilterChange = useCallback(
    (newFilters: RepositoryFilters) => {
      setQuery({ filters: newFilters, sort });
    },
    [setQuery, sort]
  );

  const getPaginationUrl = useCallback(
    (selectedPage: number) => {
      const newQueryString = qs.stringify({
        ...query,
        page: selectedPage,
      });
      return `${pathname}?${newQueryString}`;
    },
    [pathname, query]
  );

  return (
    <Page
      repositories={repositories}
      setRepositories={setRepositories}
      filters={filters}
      getPaginationUrl={getPaginationUrl}
      handleSortChange={handleSortChange}
      isAscending={sort === 'updated_on'}
      onFilterChange={handleFilterChange}
      page={page || null}
      isLoading={showTableLoading}
      isError={!!error}
      resourceData={data}
    />
  );
};

export default ProfileRepositoriesRoute;
